import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { EventAvailable } from "@mui/icons-material";
import dayjs from "dayjs";
import type { BookableSession } from "./types";
import { bookOneOffSession } from "../AgilityClubs/api";
import { buildCompactInvoiceReference } from "../AgilityClubs/invoicePaymentUtils";
import type { BookOneOffSessionResponse } from "../AgilityClubs/types";
import ConfirmSessionBookingDialog, {
  ConfirmSessionBookingDetails,
} from "../AgilityClubs/ConfirmSessionBookingDialog";

const MAX_PLACES_NO_CAPACITY = 20;

function maxBookablePlaces(spacesRemaining: number | null | undefined): number {
  if (spacesRemaining != null) {
    return Math.max(0, spacesRemaining);
  }
  return MAX_PLACES_NO_CAPACITY;
}

interface BookableSessionsSectionProps {
  sessions: BookableSession[];
  onBooked: (result: BookOneOffSessionResponse) => void;
  onRefresh: () => void;
}

const BookableSessionsSection: React.FC<BookableSessionsSectionProps> = ({
  sessions,
  onBooked,
  onRefresh,
}) => {
  const [bookingId, setBookingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmSession, setConfirmSession] = useState<BookableSession | null>(
    null,
  );

  if (!sessions.length) {
    return null;
  }

  const confirmDetails: ConfirmSessionBookingDetails | null = confirmSession
    ? {
        name: confirmSession.name,
        dateLabel: dayjs(confirmSession.session_date).format(
          "DD MMM YYYY, HH:mm",
        ),
        fee: confirmSession.fee,
        notes: confirmSession.notes,
        maxPlaces: maxBookablePlaces(confirmSession.spaces_remaining),
      }
    : null;

  const handleBook = async (session: BookableSession, quantity: number) => {
    try {
      setBookingId(session.id);
      setError(null);
      const result = await bookOneOffSession({
        one_off_session_id: session.id,
        quantity,
      });
      setConfirmSession(null);
      onBooked(result);
      onRefresh();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: string } })
        ?.response?.status;
      const data = (err as { response?: { data?: string } })?.response?.data;
      if (status === 409) {
        setError(
          typeof data === "string" && data
            ? data
            : "That session is full. Please refresh and try again.",
        );
      } else if (status === 400 && typeof data === "string") {
        setError(data);
      } else {
        setError("Could not complete booking. Please try again.");
      }
      onRefresh();
    } finally {
      setBookingId(null);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", fontWeight: 700, color: "#667eea" }}
      >
        <EventAvailable sx={{ mr: 1, fontSize: 22 }} />
        Sessions you can book
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        {sessions.map((session) => {
          const placesHeld = session.my_places_count ?? 0;
          const full =
            session.spaces_remaining != null && session.spaces_remaining <= 0;
          const dateLabel = dayjs(session.session_date).format(
            "DD MMM YYYY, HH:mm",
          );
          const invoiceIds =
            session.my_invoice_ids?.length
              ? session.my_invoice_ids
              : session.my_invoice_id != null
                ? [session.my_invoice_id]
                : [];

          return (
            <Paper
              key={session.id}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2 }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {session.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dateLabel}
                  </Typography>
                  {session.notes?.trim() && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                    >
                      {session.notes.trim()}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    £{session.fee.toFixed(2)} per place
                    {session.capacity != null && (
                      <>
                        {" · "}
                        {session.spaces_remaining ?? 0} of {session.capacity}{" "}
                        places left
                      </>
                    )}
                  </Typography>
                </Box>
                <Stack spacing={1} alignItems={{ xs: "stretch", sm: "flex-end" }}>
                  {placesHeld > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ sm: "flex-end" }}>
                      <Chip
                        label={
                          placesHeld === 1
                            ? "1 place booked"
                            : `${placesHeld} places booked`
                        }
                        color="success"
                        size="small"
                      />
                      {invoiceIds.map((id) => (
                        <Chip
                          key={id}
                          label={buildCompactInvoiceReference([id])}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {session.my_invoice_paid === false && (
                        <Chip label="Payment due" color="warning" size="small" />
                      )}
                    </Stack>
                  )}
                  {!full && (
                    <Button
                      variant="contained"
                      disabled={bookingId === session.id}
                      onClick={() => setConfirmSession(session)}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      {placesHeld > 0 ? "Book more places" : "Book place"}
                    </Button>
                  )}
                  {full && placesHeld === 0 && (
                    <Chip label="Full" size="small" color="default" />
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <ConfirmSessionBookingDialog
        open={confirmSession != null}
        details={confirmDetails}
        confirming={bookingId != null}
        onClose={() => setConfirmSession(null)}
        onConfirm={(quantity) => {
          if (confirmSession) {
            handleBook(confirmSession, quantity);
          }
        }}
      />
    </Box>
  );
};

export default BookableSessionsSection;
